import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { VerifyPayeeCheckResponseCode } from '@seapoint/server/enums';
import { TriangleAlert } from 'lucide-react';
import { ComponentProps } from 'react';
import { Button } from '../../../design-system/button/button';
import { Inline } from '../../../design-system/inline/inline';
import { Modal, ModalContent, ModalDescription, ModalTitle } from '../../../design-system/modal/modal';
import { Stack } from '../../../design-system/stack/stack';
import { exhaustiveCheck } from '../../../utils/exhaustive-check';
import { PayeeModalVariantConfig } from './payments-flow-modal';

type PayeeCheckConfirmationModalProps = {
  isOpen: boolean;
  variantConfig: PayeeModalVariantConfig;
  isLoading: boolean;
  onClose: () => void;
  onEditDetails: () => Promise<void>;
  onContinue: () => void;
};

export const PayeeCheckConfirmationModal = ({
  isOpen,
  variantConfig,
  isLoading,
  onClose,
  onEditDetails,
  onContinue,
}: PayeeCheckConfirmationModalProps) => {
  const { ctaText } = getModalContent(variantConfig);

  return (
    <Modal open={isOpen} onOpenChange={onClose}>
      <ModalContent className="h-max w-11/12 sm:w-[520px]">
        <VisuallyHidden>
          <ModalTitle>Payee check confirmation modal</ModalTitle>

          <ModalDescription>Payee check confirmation modal</ModalDescription>
        </VisuallyHidden>

        <Stack space={4} align="center" className="bg-white p-7">
          <Inline justify="center" className="h-[120px] w-full py-5">
            <div className="h-20 w-20 rounded-full bg-seapoint-gray-background-modal p-6">
              <TriangleAlert strokeWidth={1.5} className="h-full w-full" />
            </div>
          </Inline>

          <Stack space={2} align="stretch" className="w-full">
            <h3 className="text-xl font-medium leading-5 tracking-tight text-black">
              Are you sure you want to continue?
            </h3>

            <p className="text-base font-medium leading-5 tracking-tight text-seapoint-gray">
              Paying this person or business may lead to your money being sent to the wrong account.
            </p>

            <p className="text-base font-medium leading-5 tracking-tight text-seapoint-gray">
              We may not be able to recover this money for you.
            </p>
          </Stack>

          <Stack space={2} align="stretch" className="w-full">
            <Button variant="default" size="lg" isLoading={isLoading} onClick={onEditDetails} className="w-full">
              {ctaText}
            </Button>

            <Button variant="destructive" size="lg" onClick={onContinue}>
              Continue Anyway
            </Button>
          </Stack>
        </Stack>
      </ModalContent>
    </Modal>
  );
};

const getModalContent = (variantConfig: ComponentProps<typeof PayeeCheckConfirmationModal>['variantConfig']) => {
  const { variant } = variantConfig;

  switch (variant) {
    case VerifyPayeeCheckResponseCode.CLOSE_MATCH:
      return {
        ctaText: 'Change and Continue',
      };
    case VerifyPayeeCheckResponseCode.NO_MATCH:
    case VerifyPayeeCheckResponseCode.CHECK_NOT_POSSIBLE:
    case VerifyPayeeCheckResponseCode.INVALID:
    case VerifyPayeeCheckResponseCode.GENERAL:
    case VerifyPayeeCheckResponseCode.INTERNAL_ERROR:
      return {
        ctaText: 'Edit details',
      };
    default:
      return exhaustiveCheck(variant);
  }
};
